import {IOGateway} from '@interopio/gateway';
import type {Node, NodeConnection, NodeConnections} from './types.ts';

type NodeValue = Omit<Node, 'users'> & {
    users: Set<string>,
    memberId: number,
    lastAccess: number
}

export default class InMemoryNodeConnections implements NodeConnections {
    readonly #logger: IOGateway.Logging.Logger;
    readonly #nodes = new Map<string, NodeValue>();
    readonly #nodesByEndpoint = new Map<string, string>();
    #memberIds: number = 0;
    readonly #timeout: number;

    constructor(logger: IOGateway.Logging.Logger,
                timeout: number = 60000) {
        this.#logger = logger;
        this.#timeout = timeout;
    }

    announce(nodes: Node[]): NodeConnection[] {
        for (const node of nodes) {
            const {node: nodeId, users, endpoint} = node;
            const foundId = this.#nodesByEndpoint.get(endpoint);
            if (foundId) {
                if (foundId !== nodeId) {
                    this.#logger.warn(`endpoint ${endpoint} clash. replacing node ${foundId} with ${nodeId}`);
                    this.#nodesByEndpoint.set(endpoint, nodeId);
                    this.#nodes.delete(foundId);
                }
            } else {
                this.#logger.info(`endpoint ${endpoint} announced for ${nodeId}`);
                this.#nodesByEndpoint.set(endpoint, nodeId);
            }
            this.#nodes.set(nodeId, this.updateNode(node, new Set<string>(users ?? []), nodeId, this.#nodes.get(nodeId)));
        }
        this.cleanupOldNodes();
        const sortedNodes = this.sortedNodeValues();
        return nodes.map((e) => {
            const {node} = e;
            const connect = this.findConnections(sortedNodes, this.#nodes.get(node));
            return {node, connect};
        });
    }

    find(nodeId: string): Node[] | undefined {
        const e = this.#nodes.get(nodeId);
        if (e !== undefined) {
            const sortedNodes = this.sortedNodeValues();
            const {node} = e;
            return this.findConnections(sortedNodes, this.#nodes.get(node));
        }
        return undefined;
    }

    remove(nodeId: string) {
        const removed = this.#nodes.get(nodeId);
        if (removed !== undefined) {
            this.#nodes.delete(nodeId);
            const endpoint = removed.endpoint;
            this.#nodesByEndpoint.delete(endpoint);
            this.#logger.info(`endpoint ${endpoint} removed for ${nodeId}`);
            return true;
        }
        return false;
    }

    private updateNode(newNode: Node, users: Set<string>, _key: string, oldNode?: NodeValue): NodeValue {
        const node: Omit<NodeValue, 'lastAccess' | 'users'> = oldNode ?? {...newNode, memberId: this.#memberIds++};
        return {...node, users, lastAccess: Date.now()};
    }

    private sortedNodeValues() {
        return Array.from(this.#nodes.values()).sort((a, b) => a.memberId - b.memberId);
    }

    private cleanupOldNodes() {
        const threshold = Date.now() - this.#timeout;
        for (const [nodeId,v] of this.#nodes) {
            if (v.lastAccess < threshold) {
                if (this.#logger.enabledFor('debug')) {
                    this.#logger.debug(`${nodeId} expired - no announcement since ${new Date(v.lastAccess).toISOString()}, timeout is ${this.#timeout} ms.`);
                }
                this.#nodes.delete(nodeId);
            }
        }
    }

    private findConnections(sortedNodes: NodeValue[], node?: NodeValue): Node[] {
        return sortedNodes.reduce((l, c) => {
            if (node !== undefined && c.memberId < node.memberId) {
                const intersection = new Set(c.users);
                node.users.forEach(user => {
                    if (!c.users.has(user)) {
                        intersection.delete(user);
                    }
                });
                c.users.forEach(user => {
                    if (!node.users.has(user)) {
                        intersection.delete(user);
                    }
                });
                if (intersection.size > 0) {
                    const e: Node = {node: c.node, endpoint: c.endpoint};
                    return l.concat(e);
                }
            }
            return l;
        }, new Array<Node>());
    }
}
